import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Zap, X, Check, SkipForward, ChevronRight, CircleAlert, Globe, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { renderArticleContent } from '../utils/articleRenderer.jsx';
import {
  adminHumanizeBatchStart, adminHumanizeBatchLatest,
  adminHumanizeBatchItem, adminHumanizeBatchDecide, adminIndexNowSubmit, adminSitemapResubmit,
} from '../services/api';

export const AI_LEVEL_META = {
  human:   { label: 'Emberi',       dot: 'bg-emerald-400', text: 'text-emerald-300' },
  mixed:   { label: 'Vegyes',       dot: 'bg-yellow-400',  text: 'text-yellow-300' },
  machine: { label: 'Gépi hatású',  dot: 'bg-red-500',     text: 'text-red-300' },
};

export const AiScoreBadge = ({ score = 0, level = 'human', testId }) => {
  const meta = AI_LEVEL_META[level] || AI_LEVEL_META.human;
  return (
    <span
      data-testid={testId}
      title={`Gépiesség: ${score}/100 — minél alacsonyabb, annál emberibb a szöveg`}
      className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest ${meta.text} bg-white/5 px-2 py-0.5 rounded`}
    >
      <span className={`h-2 w-2 rounded-full ${meta.dot}`} />
      {meta.label} · {score}
    </span>
  );
};

const STATUS_LABEL = {
  pending: 'Sorban',
  ready:   'Átnézésre vár',
  saved:   'Mentve',
  skipped: 'Elvetve',
  error:   'Hiba',
};

export const useHumanizeBatch = (adminKey, onPostSaved) => {
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const [reviewPostId, setReviewPostId] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { job: latest } = await adminHumanizeBatchLatest(adminKey);
      setJob(latest);
      return latest;
    } catch {
      return null;
    }
  }, [adminKey]);

  useEffect(() => { refresh(); }, [refresh]);

  // Futás közben 6 másodpercenként frissítjük a folyamatjelzőt.
  useEffect(() => {
    if (job?.status !== 'running') return undefined;
    const t = setInterval(refresh, 6000);
    return () => clearInterval(t);
  }, [job?.status, refresh]);

  const start = async (scope = 'unhumanized') => {
    setStarting(true);
    try {
      const { total } = await adminHumanizeBatchStart(adminKey, scope);
      toast.success(`Kötegelt átírás elindult — ${total} cikk. Dolgozhatsz tovább, a folyamat a háttérben fut.`);
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'A kötegelt átírás nem indult el.');
    } finally {
      setStarting(false);
    }
  };

  return { adminKey, job, starting, start, refresh, reviewPostId, setReviewPostId, onPostSaved };
};

export const HumanizeBatchBar = ({ batch, posts }) => {
  const { job, starting, start, setReviewPostId, adminKey } = batch;
  const [indexing, setIndexing] = useState(false);
  const onIndexNow = async () => {
    setIndexing(true);
    try {
      const r = await adminIndexNowSubmit(adminKey);
      toast.success(`${r.submitted} URL bejelentve a keresőknek (Bing, Yandex).`, {
        description: 'A Google a sitemap alapján frissít. Azonnali indexeléshez: Search Console → URL-ellenőrzés → Indexelés kérése.',
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'A bejelentés nem sikerült.');
    } finally {
      setIndexing(false);
    }
  };
  const ready = useMemo(() => (job?.items || []).filter((i) => i.status === 'ready'), [job]);
  const running = job?.status === 'running';
  const pendingCount = posts.filter((p) => !p.humanized_at).length;

  const [resubmitting, setResubmitting] = useState(false);
  const [gsc, setGsc] = useState(null);
  const onResubmit = async () => {
    setResubmitting(true);
    try {
      const r = await adminSitemapResubmit(adminKey);
      setGsc(r);
      toast.success(`Sitemap ellenőrizve — ${r.sitemap_urls} URL. ${r.indexnow_submitted} URL bejelentve (Bing, Yandex).`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Az újraküldés nem sikerült.');
    } finally {
      setResubmitting(false);
    }
  };

  return (
    <div
      data-testid="humanize-batch-bar"
      className="mb-5 rounded-xl border border-emerald-700/40 bg-emerald-500/5 p-4 flex flex-wrap items-center gap-4"
    >
      <div className="flex-1 min-w-[240px]">
        <div className="text-sm font-bold text-white flex items-center gap-2">
          <Zap size={15} className="text-emerald-300" />
          Kötegelt emberibbé tétel
        </div>
        {running ? (
          <>
            <p className="text-xs text-gray-400 mt-1">
              Átírás folyamatban — {job.processed}/{job.total} cikk kész. Cikkenként 2-4 perc,
              nyugodtan zárd be az oldalt.
            </p>
            <div className="mt-2 h-1.5 w-full rounded-full bg-gray-800 overflow-hidden">
              <div
                className="h-full bg-emerald-500 transition-all"
                style={{ width: `${job.total ? (job.processed / job.total) * 100 : 0}%` }}
              />
            </div>
          </>
        ) : (
          <p className="text-xs text-gray-400 mt-1">
            {ready.length > 0
              ? `${ready.length} átírt cikk vár a jóváhagyásodra. Semmi nem mentődött automatikusan.`
              : `${pendingCount} cikk nincs még átírva. Egy kattintás, és a háttérben mindegyiket átnézi.`}
          </p>
        )}
      </div>
      <div className="flex items-center gap-2">
        {ready.length > 0 && !running && (
          <Button
            data-testid="humanize-batch-review-btn"
            onClick={() => setReviewPostId(ready[0].post_id)}
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
          >
            Átnézés ({ready.length}) <ChevronRight size={14} className="ml-1" />
          </Button>
        )}
        <Button
          data-testid="indexnow-submit-btn"
          onClick={onIndexNow}
          disabled={indexing}
          variant="outline"
          className="border-sky-600/50 text-sky-200 hover:text-white hover:bg-sky-600/20"
        >
          {indexing
            ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Bejelentés…</>
            : <><Globe size={14} className="mr-1.5" /> Keresőknek bejelentés</>}
        </Button>
        <Button
          data-testid="sitemap-resubmit-btn"
          onClick={onResubmit}
          disabled={resubmitting}
          variant="outline"
          className="border-indigo-600/50 text-indigo-200 hover:text-white hover:bg-indigo-600/20"
        >
          {resubmitting
            ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Ellenőrzés…</>
            : <><RefreshCw size={14} className="mr-1.5" /> Google sitemap újraküldés</>}
        </Button>
        <Button
          data-testid="humanize-batch-start-btn"
          onClick={() => start('unhumanized')}
          disabled={starting || running || pendingCount === 0}
          variant="outline"
          className="border-emerald-600/50 text-emerald-200 hover:text-white hover:bg-emerald-600/20"
        >
          {starting || running
            ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Fut…</>
            : <><Zap size={14} className="mr-1.5" /> Összes átírása</>}
        </Button>
      </div>

      <Dialog open={!!gsc} onOpenChange={(o) => !o && setGsc(null)}>
        <DialogContent data-testid="gsc-steps-dialog" className="max-w-xl bg-[#0b0b0d] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Google újraindexelés — így fejezd be</DialogTitle>
            <DialogDescription className="text-gray-400">
              A sitemap {gsc?.sitemap_ok ? 'rendben' : 'HIBÁS — nézd meg a logot'} ·{' '}
              {gsc?.sitemap_urls} URL · IndexNow (Bing, Yandex): {gsc?.indexnow_submitted || 0} URL bejelentve
            </DialogDescription>
          </DialogHeader>
          <ol className="space-y-2.5 text-sm text-gray-300 list-decimal pl-5">
            {(gsc?.gsc_steps || []).map((s, i) => <li key={i}>{s}</li>)}
          </ol>
          <div className="flex items-center gap-2 pt-2">
            <a
              data-testid="gsc-open-link"
              href="https://search.google.com/search-console/sitemaps"
              target="_blank"
              rel="noreferrer"
              className="text-sky-300 text-sm font-bold hover:underline"
            >
              Search Console megnyitása →
            </a>
            <a
              data-testid="sitemap-open-link"
              href={gsc?.sitemap}
              target="_blank"
              rel="noreferrer"
              className="ml-auto text-gray-400 text-xs hover:text-white"
            >
              sitemap.xml megnyitása
            </a>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const HumanizeBatchItemBadge = ({ job, postId }) => {
  const item = (job?.items || []).find((i) => i.post_id === postId);
  if (!item || item.status === 'pending') return null;
  const color = item.status === 'ready'
    ? 'text-emerald-300 bg-emerald-500/10'
    : item.status === 'error'
      ? 'text-red-300 bg-red-500/10'
      : 'text-gray-400 bg-white/5';
  return (
    <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded ${color}`}>
      {STATUS_LABEL[item.status]}
    </span>
  );
};

export const HumanizeReviewDialog = ({ adminKey, batch }) => {
  const { job, reviewPostId, setReviewPostId, refresh, onPostSaved } = batch;
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deciding, setDeciding] = useState(false);

  const readyList = useMemo(() => (job?.items || []).filter((i) => i.status === 'ready'), [job]);
  const current = readyList.find((i) => i.post_id === reviewPostId) || null;

  useEffect(() => {
    if (!reviewPostId || !job?.job_id) { setItem(null); return; }
    let active = true;
    setLoading(true);
    adminHumanizeBatchItem(adminKey, job.job_id, reviewPostId)
      .then(({ item: it }) => { if (active) setItem(it); })
      .catch(() => toast.error('Az átírt szöveg betöltése nem sikerült.'))
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [adminKey, job?.job_id, reviewPostId]);

  const goNext = async (decidedId) => {
    const updated = await refresh();
    const next = (updated?.items || []).find((i) => i.status === 'ready' && i.post_id !== decidedId);
    setReviewPostId(next ? next.post_id : null);
    if (!next) toast.success('Végeztél — nincs több átnézésre váró cikk.');
  };

  const decide = async (action) => {
    if (!reviewPostId) return;
    setDeciding(true);
    try {
      await adminHumanizeBatchDecide(adminKey, job.job_id, reviewPostId, action);
      toast.success(action === 'save' ? 'Mentve — az eredeti megőrizve.' : 'Elvetve, a cikk változatlan.');
      if (action === 'save' && onPostSaved) onPostSaved();
      await goNext(reviewPostId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'A művelet nem sikerült.');
    } finally {
      setDeciding(false);
    }
  };

  return (
    <Dialog open={!!reviewPostId} onOpenChange={(open) => { if (!open) setReviewPostId(null); }}>
      <DialogContent data-testid="humanize-review-dialog" className="max-w-3xl max-h-[88vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap size={18} className="text-emerald-600" />
            {current?.title || 'Átírt cikk'}
          </DialogTitle>
          <DialogDescription>
            {readyList.length} cikk vár jóváhagyásra. Mentés után az eredeti változat megőrződik,
            és a szerkesztőben bármikor visszaállítható.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto rounded-xl border border-gray-200 bg-white p-6 text-gray-900">
          {loading ? (
            <div className="py-16 text-center text-gray-500">
              <Loader2 className="animate-spin mx-auto mb-2" size={22} /> Betöltés…
            </div>
          ) : item?.status === 'error' ? (
            <div className="py-16 text-center text-red-600 flex flex-col items-center gap-2">
              <CircleAlert size={22} /> {item.error || 'Az átírás nem sikerült.'}
            </div>
          ) : (
            <>
              {item?.excerpt && (
                <div className="mb-5 rounded-lg bg-emerald-50 border border-emerald-200 p-4">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 mb-1">
                    Új bevezető
                  </div>
                  <p className="text-sm text-gray-800" data-testid="humanize-review-excerpt">{item.excerpt}</p>
                </div>
              )}
              <div className="prose-like">{renderArticleContent(item?.content || '')}</div>
            </>
          )}
        </div>

        <div className="flex flex-wrap justify-end gap-2 pt-2">
          <Button
            data-testid="humanize-review-close"
            variant="ghost"
            onClick={() => setReviewPostId(null)}
            className="text-gray-400"
          >
            <X size={14} className="mr-1.5" /> Bezárás
          </Button>
          <Button
            data-testid="humanize-review-skip"
            variant="outline"
            disabled={deciding}
            onClick={() => decide('skip')}
          >
            <SkipForward size={14} className="mr-1.5" /> Elvetés
          </Button>
          <Button
            data-testid="humanize-review-save"
            disabled={deciding || loading || item?.status === 'error'}
            onClick={() => decide('save')}
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
          >
            {deciding
              ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Mentés…</>
              : <><Check size={14} className="mr-1.5" /> Mentés</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
